import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Plus, Search, X, IndianRupee, Receipt, Calendar, Trash2 } from 'lucide-react';
import { api } from '../lib/api';
import { authService as auth } from '../lib/auth';

const OldFeeCollection = () => {
  const [records, setRecords] = useState([]);
  const [totalAmount, setTotalAmount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilter, setDateFilter] = useState('');

  const currentUser = auth.getCurrentUser();
  const isManagerOrAdmin = ['admin', 'manager', 'offline_manager'].includes(currentUser?.role?.toLowerCase());

  const paymentModes = ['cash', 'upi', 'credit_card', 'debit_card', 'bank_transfer', 'cheque'];

  const [formData, setFormData] = useState({
    student_name: '',
    phone: '',
    old_slip_number: '',
    amount: '',
    payment_mode: 'cash',
    utr_number: '',
    payment_date: new Date().toISOString().split('T')[0],
    course: '',
    notes: ''
  });

  useEffect(() => {
    fetchRecords();
  }, [dateFilter]);

  const fetchRecords = async () => {
    try {
      const params = {};
      if (dateFilter) {
        params.start_date = dateFilter;
        params.end_date = dateFilter;
      }
      if (searchTerm) params.search = searchTerm;
      const res = await api.getOldFeeCollections(params);
      setRecords(res.data.records || []);
      setTotalAmount(res.data.total_amount || 0);
    } catch (err) {
      toast.error('Failed to load records');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.createOldFeeCollection({
        ...formData,
        amount: parseFloat(formData.amount)
      });
      toast.success('Old fee entry recorded');
      setShowModal(false);
      setFormData({
        student_name: '', phone: '', old_slip_number: '', amount: '',
        payment_mode: 'cash', utr_number: '',
        payment_date: new Date().toISOString().split('T')[0], course: '', notes: ''
      });
      fetchRecords();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save entry');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this record?')) return;
    try {
      await api.deleteOldFeeCollection(id);
      toast.success('Record deleted');
      fetchRecords();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const handleSearch = () => {
    fetchRecords();
  };

  const formatMode = (m) => {
    if (!m) return 'Cash';
    if (m === 'upi') return 'UPI';
    if (m === 'credit_card') return 'Credit Card';
    if (m === 'debit_card') return 'Debit Card';
    return m.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  return (
    <div className="space-y-6" data-testid="old-fee-collection-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Old Fee Collection</h1>
          <p className="text-slate-600">Record pending fee payments from old admissions (before system started)</p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="flex items-center gap-2 bg-teal-600 text-white px-4 py-2 rounded-lg hover:bg-teal-700 transition"
          data-testid="add-old-fee-btn"
        >
          <Plus size={20} />
          New Entry
        </button>
      </div>

      {/* Summary Card */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-green-50 rounded-lg">
            <IndianRupee size={24} className="text-green-600" />
          </div>
          <div>
            <p className="text-sm text-slate-500">Total Old Fee Collected {dateFilter ? `on ${new Date(dateFilter).toLocaleDateString()}` : '(All Time)'}</p>
            <p className="text-2xl font-bold text-green-700" data-testid="old-fee-total">₹{totalAmount.toLocaleString()}</p>
          </div>
          <div className="ml-auto text-sm text-slate-500">
            {records.length} entries
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
            <input
              type="text"
              placeholder="Search by name, phone, slip number..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg"
              data-testid="search-old-fee"
            />
          </div>
          <input
            type="date"
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
            className="px-3 py-2 border border-slate-200 rounded-lg"
            data-testid="filter-date-old-fee"
          />
          {dateFilter && (
            <button onClick={() => setDateFilter('')} className="px-3 py-2 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-lg border border-slate-200">
              <X size={16} />
            </button>
          )}
        </div>
      </div>

      {/* Records Table */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-slate-500">Loading...</div>
        ) : records.length === 0 ? (
          <div className="p-8 text-center text-slate-500">
            <Receipt size={48} className="mx-auto mb-3 text-slate-300" />
            <p>No old fee entries found</p>
            <p className="text-sm mt-1">Click "New Entry" to record a legacy fee payment</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  <th className="px-4 py-3 text-left font-medium">Student</th>
                  <th className="px-4 py-3 text-left font-medium">Old Slip No.</th>
                  <th className="px-4 py-3 text-left font-medium">Amount</th>
                  <th className="px-4 py-3 text-left font-medium">Mode</th>
                  <th className="px-4 py-3 text-left font-medium">UTR</th>
                  <th className="px-4 py-3 text-left font-medium">Date</th>
                  <th className="px-4 py-3 text-left font-medium">Collected By</th>
                  {isManagerOrAdmin && <th className="px-4 py-3 text-left font-medium">Actions</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {records.map((r) => (
                  <tr key={r.id} className="hover:bg-slate-50">
                    <td className="px-4 py-3">
                      <p className="font-medium text-slate-800">{r.student_name}</p>
                      {r.phone && <p className="text-xs text-slate-500">{r.phone}</p>}
                      {r.course && <p className="text-xs text-slate-400">{r.course}</p>}
                    </td>
                    <td className="px-4 py-3 font-mono text-slate-700">{r.old_slip_number}</td>
                    <td className="px-4 py-3 font-semibold text-green-600">₹{r.amount?.toLocaleString()}</td>
                    <td className="px-4 py-3 text-slate-600">{formatMode(r.payment_mode)}</td>
                    <td className="px-4 py-3 text-slate-500">{r.utr_number || '-'}</td>
                    <td className="px-4 py-3 text-slate-600">{r.payment_date}</td>
                    <td className="px-4 py-3 text-slate-600">{r.collected_by_name}</td>
                    {isManagerOrAdmin && (
                      <td className="px-4 py-3">
                        <button onClick={() => handleDelete(r.id)} className="text-red-500 hover:text-red-700 p-1" data-testid={`delete-old-fee-${r.id}`}>
                          <Trash2 size={16} />
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add Entry Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center p-4 border-b">
              <h2 className="text-lg font-semibold">Record Old Fee Payment</h2>
              <button onClick={() => setShowModal(false)} className="p-1 hover:bg-slate-100 rounded">
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Student Name *</label>
                <input type="text" value={formData.student_name}
                  onChange={(e) => setFormData({...formData, student_name: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                  required data-testid="old-fee-student-name" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Phone</label>
                  <input type="tel" value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                    data-testid="old-fee-phone" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Course</label>
                  <input type="text" value={formData.course}
                    onChange={(e) => setFormData({...formData, course: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                    data-testid="old-fee-course" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Old Slip / Receipt Number *</label>
                <input type="text" value={formData.old_slip_number}
                  onChange={(e) => setFormData({...formData, old_slip_number: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                  placeholder="Original receipt/slip number"
                  required data-testid="old-fee-slip-number" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Amount *</label>
                  <input type="number" value={formData.amount}
                    onChange={(e) => setFormData({...formData, amount: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                    required data-testid="old-fee-amount" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Payment Date *</label>
                  <input type="date" value={formData.payment_date}
                    onChange={(e) => setFormData({...formData, payment_date: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                    required data-testid="old-fee-date" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Payment Mode</label>
                <select value={formData.payment_mode}
                  onChange={(e) => setFormData({...formData, payment_mode: e.target.value, utr_number: ''})}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                  data-testid="old-fee-payment-mode">
                  {paymentModes.map(m => (
                    <option key={m} value={m}>{formatMode(m)}</option>
                  ))}
                </select>
              </div>
              {formData.payment_mode && formData.payment_mode !== 'cash' && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">UTR Number *</label>
                  <input type="text" value={formData.utr_number}
                    onChange={(e) => setFormData({...formData, utr_number: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                    placeholder="Transaction reference number"
                    required data-testid="old-fee-utr" />
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Notes</label>
                <textarea value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg" rows={2}
                  data-testid="old-fee-notes" />
              </div>
              <div className="flex gap-3 pt-4">
                <button type="button" onClick={() => setShowModal(false)} className="flex-1 px-4 py-2 border border-slate-200 rounded-lg hover:bg-slate-50">Cancel</button>
                <button type="submit" className="flex-1 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700" data-testid="old-fee-submit">Save Entry</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default OldFeeCollection;
